import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { getConversation } from '../api/messagesService';
import { useAuth } from '../contexts/AuthContext';

const MessageThread: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const id = conversationId ? parseInt(conversationId, 10) : NaN;
  const { user } = useAuth();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['messages-conversation', id],
    queryFn: () => getConversation(id),
    enabled: Number.isFinite(id),
  });

  const conv = data?.data?.conversation;
  const messages = data?.data?.messages || [];

  return (
    <Layout>
      <div className="px-4 sm:px-0 max-w-3xl">
        <Link to="/messages" className="text-sm text-primary-600 hover:text-primary-700">
          ← Back to messages
        </Link>
        <h1 className="text-xl font-bold text-gray-900 mt-4">
          {conv?.name || 'Conversation'}
        </h1>

        {isLoading && <p className="mt-6 text-sm text-gray-600">Loading…</p>}
        {isError && (
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Failed to load thread.{' '}
            <button type="button" className="font-medium underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !isError && (
          <div className="mt-6 space-y-3">
            {messages.length === 0 ? (
              <p className="text-sm text-gray-500">No messages in this conversation.</p>
            ) : (
              messages.map((m) => {
                const mine = m.senderId === user?.id;
                return (
                  <div
                    key={m.id}
                    className={`rounded-lg px-3 py-2 text-sm ${mine ? 'bg-primary-50 ml-8' : 'bg-gray-100 mr-8'}`}
                  >
                    <div className="text-xs text-gray-500 mb-1">
                      {m.sender.firstName} {m.sender.lastName} ·{' '}
                      {new Date(m.createdAt).toLocaleString()}
                    </div>
                    <div className="text-gray-900 whitespace-pre-wrap">{m.content}</div>
                  </div>
                );
              })
            )}
            <p className="text-xs text-gray-400 pt-2">
              Sending messages from the web hub is not available yet; use the mobile app to reply.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MessageThread;
